import { useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import { send } from "@emailjs/browser";
import CloseIcon from "@mui/icons-material/Close";
import Logo from "./Logo";
import NotificationService from "./notification/NotificationService";

interface Props {
  handleClose: () => void;
}

const initialForm = {
  name: "",
  email: "",
  message: "",
};

const initialErrors = {
  name: "",
  email: "",
  message: "",
};

export default function GetInTouchModal({ handleClose }: Props) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState(initialErrors);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: "" }); // Clear error on change
  };

  const validate = () => {
    const newErrors = {
      name: form.name.trim() ? "" : "Name is required.",
      email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
        ? ""
        : "Invalid email address.",
      message: form.message.trim() ? "" : "Message cannot be empty",
    };

    setErrors(newErrors);
    return Object.values(newErrors).every((e) => e === "");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await send(
        "service_agle8mt",
        "template_xdn9b8b",
        form,
        "Jc-uQZgnH1pLjVD7X"
      );

      if (response.status === 200) {
        setSubmitted(true); // Show success message
      } else {
        console.error(response.status, response.text);
      }
    } catch (err) {
      NotificationService.showToast({
        severity: "error",
        text: "Failed to send message. Please contact kalyanamdk@gmail.com",
        duration: 10000,
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          borderRadius: "50px",
        },
      }}
    >
      <DialogContent sx={{ paddingBottom: 0, backgroundColor: "#AB8F6E" }}>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 18,
            right: 18,
            color: "#2c2218",
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "15px",
          }}
          noValidate
        >
          <Logo color="black" size="30px" />
          <Typography
            variant="h6"
            textAlign="center"
            fontFamily="The Season Light"
            letterSpacing={2}
            fontWeight={800}
            marginTop={2}
          >
            GET IN TOUCH
          </Typography>

          <FormField
            label="NAME"
            name="name"
            value={form.name}
            onChange={handleChange}
            error={errors.name}
            disabled={submitted}
          />
          <FormField
            label="EMAIL"
            name="email"
            value={form.email}
            onChange={handleChange}
            error={errors.email}
            type="email"
            disabled={submitted}
          />
          <FormField
            label="TELL US ABOUT YOUR BUSINESS"
            name="message"
            value={form.message}
            onChange={handleChange}
            error={errors.message}
            multiline
            minRows={3}
            background="#faead77a"
            disabled={submitted}
          />
          {submitted ? (
            <Typography
              variant="body2"
              style={{
                color: "#2c2218",
                margin: "20px 0",
                fontFamily: "Belleza",
                fontSize: "0.9rem",
              }}
            >
              Message sent successfully!
            </Typography>
          ) : (
            <Button
              type="submit"
              disabled={loading}
              variant="contained"
              style={{
                backgroundColor: "#2c2218",
                color: "#C09C72",
                margin: "0",
                width: "100px",
                fontFamily: "Belleza",
                fontSize: "0.8rem",
                padding: "5px",
                letterSpacing: "2px",
              }}
            >
              {loading ? "Sending..." : "Send"}
            </Button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface FormFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  error?: string;
  type?: string;
  multiline?: boolean;
  minRows?: number;
  background?: string;
  disabled?: boolean;
}

function FormField({
  label,
  name,
  value,
  onChange,
  error,
  type = "text",
  multiline = false,
  minRows = 1,
  background,
  disabled,
}: FormFieldProps) {
  // Add extra margin for the "message" field label
  const isMessageField = name === "message";
  return (
    <>
      <Typography
        sx={{
          marginTop: "20px",
          marginBottom: isMessageField ? "24px" : "8px", // More bottom margin for message label
        }}
        textAlign="center"
        fontFamily="The Season Light"
        letterSpacing={2}
        fontWeight={800}
        fontSize={"0.8rem"}
      >
        {label}
      </Typography>
      <TextField
        name={name}
        value={value}
        disabled={disabled}
        onChange={onChange}
        type={type}
        fullWidth
        multiline={multiline}
        minRows={minRows}
        variant="standard"
        error={!!error}
        helperText={error}
        sx={{
          marginTop: 0,
          marginBottom: "20px",
          backgroundColor: background || "transparent",
          padding: multiline ? "10px" : undefined,
          "& .MuiInputBase-input": {
            textAlign: "center",
          },
        }}
        InputProps={{
          disableUnderline: multiline,
        }}
      />
    </>
  );
}
